export const ROUTE_PATHS = {
  HOME: '/',
  SUPPLIERS: '/suppliers',
  SUPPLIER_DETAIL: '/suppliers/:id',
  PRODUCTS: '/products',
  PRODUCT_DETAIL: '/products/:id',
  NEWS: '/news',
  NEWS_DETAIL: '/news/:id',
  CONTACT: '/contact',
  ADMIN_LOGIN: '/admin/login',
  ADMIN_DASHBOARD: '/manage/dashboard',
  ADMIN_SUPPLIERS: '/manage/suppliers',
  ADMIN_PRODUCTS: '/manage/products',
  ADMIN_NEWS: '/manage/news',
  ADMIN_ADS: '/manage/advertisements',
  ADMIN_INQUIRIES: '/manage/inquiries',
  ADMIN_SUBSCRIBERS: '/manage/subscribers',
} as const;

export interface Supplier {
  id: string;
  name: string;
  logo: string;
  description: string;
  shortDescription: string;
  products: string[];
  categories: string[];
  location: string;
  country: string;
  website: string;
  email: string;
  phone: string;
  founded: string;
  employees: string;
  certifications: string[];
  featured: boolean;
  images: string[];
  videoUrl?: string;
}

export interface Product {
  id: string;
  name: string;
  category: string;
  supplierId: string;
  supplierName: string;
  description: string;
  shortDescription: string;
  images: string[];
  specifications: Record<string, string>;
  features: string[];
  applications: string[];
  price?: string;
  featured: boolean;
}

export interface Category {
  id: string;
  name: string;
  description: string;
  icon: string;
  productCount: number;
}

export interface NewsArticle {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
  date: string;
  image: string;
  featured: boolean;
  tags: string[];
}

export interface Advertisement {
  id: string;
  title: string;
  image: string;
  link: string;
  size: 'leaderboard' | 'rectangle' | 'skyscraper';
  position: string;
  active: boolean;
}

export const CATEGORIES: Category[] = [
  {
    id: 'security-screening',
    name: 'Security Screening',
    description: 'Advanced screening equipment and technologies for passenger and baggage security',
    icon: 'Shield',
    productCount: 45,
  },
  {
    id: 'access-control',
    name: 'Access Control',
    description: 'Biometric systems, ID verification, and perimeter security solutions',
    icon: 'Lock',
    productCount: 38,
  },
  {
    id: 'surveillance',
    name: 'Surveillance Systems',
    description: 'CCTV, video analytics, and monitoring solutions for airport security',
    icon: 'Camera',
    productCount: 52,
  },
  {
    id: 'cybersecurity',
    name: 'Cybersecurity',
    description: 'Network security, data protection, and IT infrastructure solutions',
    icon: 'Server',
    productCount: 29,
  },
  {
    id: 'terminal-equipment',
    name: 'Terminal Equipment',
    description: 'Passenger processing systems, check-in kiosks, and terminal infrastructure',
    icon: 'Building',
    productCount: 41,
  },
  {
    id: 'emergency-response',
    name: 'Emergency Response',
    description: 'Fire safety, emergency communication, and crisis management systems',
    icon: 'AlertTriangle',
    productCount: 33,
  },
];

export function filterSuppliersByCategory(suppliers: Supplier[], category: string): Supplier[] {
  if (!category || category === 'all') return suppliers;
  return suppliers.filter(supplier => 
    supplier.categories.some(cat => cat.toLowerCase().includes(category.toLowerCase()))
  );
}

export function filterProductsByCategory(products: Product[], category: string): Product[] {
  if (!category || category === 'all') return products;
  return products.filter(product => 
    product.category.toLowerCase() === category.toLowerCase()
  );
}

export function searchSuppliers(suppliers: Supplier[], query: string): Supplier[] {
  if (!query) return suppliers;
  const lowerQuery = query.toLowerCase();
  return suppliers.filter(supplier => 
    supplier.name.toLowerCase().includes(lowerQuery) ||
    supplier.description.toLowerCase().includes(lowerQuery) ||
    supplier.products.some(product => product.toLowerCase().includes(lowerQuery))
  );
}

export function searchProducts(products: Product[], query: string): Product[] {
  if (!query) return products;
  const lowerQuery = query.toLowerCase();
  return products.filter(product => 
    product.name.toLowerCase().includes(lowerQuery) ||
    product.description.toLowerCase().includes(lowerQuery) ||
    product.category.toLowerCase().includes(lowerQuery)
  );
}

export function getFeaturedSuppliers(suppliers: Supplier[]): Supplier[] {
  return suppliers.filter(supplier => supplier.featured);
}

export function getFeaturedProducts(products: Product[]): Product[] {
  return products.filter(product => product.featured);
}

export function getFeaturedNews(articles: NewsArticle[]): NewsArticle[] {
  return articles.filter(article => article.featured);
}

export function getSupplierById(suppliers: Supplier[], id: string): Supplier | undefined {
  return suppliers.find(supplier => supplier.id === id);
}

export function getProductById(products: Product[], id: string): Product | undefined {
  return products.find(product => product.id === id);
}

export function getNewsById(articles: NewsArticle[], id: string): NewsArticle | undefined {
  return articles.find(article => article.id === id);
}

export function getProductsBySupplier(products: Product[], supplierId: string): Product[] {
  return products.filter(product => product.supplierId === supplierId);
}

export function getRelatedProducts(products: Product[], currentProductId: string, category: string, limit: number = 4): Product[] {
  return products
    .filter(product => product.id !== currentProductId && product.category === category)
    .slice(0, limit);
}

export function getRelatedNews(articles: NewsArticle[], currentArticleId: string, category: string, limit: number = 3): NewsArticle[] {
  return articles
    .filter(article => article.id !== currentArticleId && article.category === category)
    .slice(0, limit);
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}